import {
    useCallback
} from 'react';

import {
    atom,
    useSetRecoilState,
    useRecoilValue,
    useRecoilState
} from 'recoil';

import {
    CURATION_INSTANCE_ID
} from './widgetConfiguration.js';


export const LoadingStatus = {
    LOADING: 'LOADING',
    COMPLETE: 'COMPLETE',
    FAILED: 'FAILED'
};

const allInstancesLoadingStatus = atom({
    key: 'allInstancesLoadingStatus',
    default: {
        statusById: {},
        errorById: {}
    },
    effects: [
        ({onSet}) => {
            onSet(({statusById, errorById}) => {
                const allSuccessful = _.every(statusById, status => status === LoadingStatus.COMPLETE);
                if (allSuccessful) {
                    RivalIQ.curation._curationLoadingCompleteAsync()
                        .catch(error => {
                            RivalIQ.logger.error(`error completing loading: ${error}`);
                        });
                } else if (RivalIQ.state.view.isExport()) {
                    const failedIds = _.keys(_.pickBy(statusById, status => status === LoadingStatus.FAILED));
                    if (!_.isEmpty(failedIds)) {
                        const errorMessages = _.map(failedIds, failedId => errorById[failedId]);
                        RivalIQ.export.failExport(errorMessages.join('; '));
                    }
                }
            });
        }
    ]
});

function statusUpdater(instanceId, updateState) {
    return function (newLoadingStatus, error) {
        updateState(state => {
            // NOTE support passing in an updater function
            let resolvedNewLoadingStatus = newLoadingStatus;
            if (_.isFunction(newLoadingStatus)) {
                resolvedNewLoadingStatus = newLoadingStatus(state);
            }

            if (!LoadingStatus[resolvedNewLoadingStatus]) {
                throw new Error(`Invalid loading status for instanceId ${instanceId}: ${resolvedNewLoadingStatus}`);
            }

            if (error && resolvedNewLoadingStatus !== LoadingStatus.FAILED) {
                throw new Error(`Passed an error with a non error state for instanceId ${instanceId}: ${resolvedNewLoadingStatus}`);
            }

            const newState = {
                statusById: _.defaults({
                    [instanceId]: resolvedNewLoadingStatus
                }, state.statusById),
                errorById: _.defaults({
                    [instanceId]: error
                }, state.errorById)
            };

            return newState;
        });
    };
}

// Gets the current status for the given instanceId. By calling this hook, your component will be re-rendered
// when the status changes.
export function useGetInstanceLoadingStatus(instanceId) {
    instanceId ||= CURATION_INSTANCE_ID;

    const currentState = useRecoilValue(allInstancesLoadingStatus);
    return currentState.statusById[instanceId];
}

// Gets the current statuses for the given instanceIds. By calling this hook, your component will be re-rendered
// when any loading status changes.
export function useGetInstanceLoadingStatuses(instanceIds) {
    const currentState = useRecoilValue(allInstancesLoadingStatus);
    return _.map(instanceIds, instanceId => currentState.statusById[instanceId]);
}

// Returns a function to set the status for the given instanceId. The returned function should not be called in the render phase.
export function useSetInstanceLoadingStatus(instanceId) {
    instanceId ||= CURATION_INSTANCE_ID;

    const updateStatus = useSetRecoilState(allInstancesLoadingStatus);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(statusUpdater(instanceId, updateStatus),
        [instanceId, updateStatus]);
}

// Gets the current status for the instanceId and a function to update it. The returned function should not be called in the render phase.
export function useInstanceLoadingStatus(instanceId) {

    instanceId ||= CURATION_INSTANCE_ID;

    const [currentState, updateState] = useRecoilState(allInstancesLoadingStatus);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updater = useCallback(statusUpdater(instanceId, updateState),
        [instanceId, updateState]);

    return [currentState.statusById[instanceId] || LoadingStatus.LOADING, updater];
}
